import { useEffect } from "react";
import { motion } from "framer-motion";
import Marquee from "@/components/sections/Marquee";
import CTA from "@/components/sections/CTA";

const values = [
  ["Integrity", "Truth before commission. Advice before transaction."],
  ["Confidentiality", "Discretion is a deliverable. Not a courtesy."],
  ["Knowledge", "Deep expertise across the aircraft lifecycle."],
  ["Precision", "Rigorous analysis. No estimates offered as facts."],
  ["Long-term Relationships", "Repeat mandates are the only measure of success."],
  ["Global Network", "Operators, MROs, financiers, appraisers — on call."],
];

export default function About() {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  return (
    <main data-testid="page-about" className="pt-32 bg-[#FAFAF9]">
      <section className="max-w-[1440px] mx-auto px-6 md:px-10 py-20 md:py-32">
        <div className="flex items-center gap-3 text-[#A5854C] mb-8">
          <div className="hairline w-12"></div>
          <span className="font-mono text-[11px] uppercase tracking-[0.35em]">About · JS Aviation Group</span>
        </div>
        <motion.h1
          initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1 }}
          className="font-serif font-light tracking-[-0.03em] text-[#0E1B2E] text-[52px] md:text-[96px] lg:text-[120px] leading-[0.95]"
        >
          Aviation intelligence,<br/><span className="italic text-[#A5854C]">beyond brokerage.</span>
        </motion.h1>
        <div className="grid grid-cols-12 gap-8 mt-20">
          <div className="col-span-12 md:col-span-6">
            <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#9AA3AE] mb-4">Philosophy</div>
            <p className="font-serif text-[24px] md:text-[32px] text-[#0E1B2E] leading-[1.3] tracking-tight">
              Every aircraft transaction is a multi-million-dollar investment decision. Our role is to ensure every decision is supported by data.
            </p>
          </div>
          <div className="col-span-12 md:col-span-6 space-y-6 text-[#445060] text-[15px] md:text-[17px] leading-relaxed">
            <p>
              JS Aviation Group is an international aviation advisory, asset trading, aircraft sourcing and transaction partner. We support buyers, sellers, lessors, financiers and operators across commercial, corporate and general aviation.
            </p>
            <p>
              Every mandate is grounded in current market value, historical comparables, engine trends, residual value forecasts, lease rates, operator history, and technical records review. Our knowledge becomes our client's competitive advantage.
            </p>
          </div>
        </div>
      </section>

      <section className="border-t border-[#0E1B2E]/10 py-20 md:py-32 bg-white">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10">
          <div className="flex items-center gap-3 text-[#A5854C] mb-14">
            <div className="hairline w-12"></div>
            <span className="font-mono text-[11px] uppercase tracking-[0.35em]">Values</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#0E1B2E]/10 border border-[#0E1B2E]/10">
            {values.map(([t, d], i) => (
              <div key={t} className="bg-white p-8 md:p-10">
                <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#9AA3AE]">
                  {String(i + 1).padStart(2, "0")} / 06
                </div>
                <h3 className="mt-8 font-serif text-[26px] text-[#0E1B2E] tracking-tight">{t}</h3>
                <p className="mt-4 text-[#6B7684] text-[14px] leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* History */}
      <section data-testid="about-history" className="border-t border-[#0E1B2E]/10 py-20 md:py-32 bg-[#F2F2EF]">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 grid grid-cols-12 gap-10">
          <div className="col-span-12 md:col-span-4">
            <div className="flex items-center gap-3 text-[#A5854C] mb-6">
              <div className="hairline w-12"></div>
              <span className="font-mono text-[11px] uppercase tracking-[0.35em]">Our history</span>
            </div>
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.4 }}
              transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
              className="font-serif font-light tracking-[-0.02em] text-[#0E1B2E] text-[36px] md:text-[56px] leading-[1.02]"
            >
              From Hong Kong<br/> to <span className="italic text-[#A5854C]">the world.</span>
            </motion.h2>
          </div>
          <div className="col-span-12 md:col-span-8 space-y-6 text-[#445060] text-[15px] md:text-[17px] leading-[1.8]">
            <p>
              Founded a few years ago in <span className="text-[#0E1B2E] font-medium">Hong Kong</span>, JS Group began as a dedicated aircraft brokerage firm with a vision to simplify and strengthen aviation transactions across global markets. Through a commitment to integrity, transparency, and exceptional client service, the company quickly established itself as a trusted partner for aircraft acquisitions, sales, leasing, and aviation consulting.
            </p>
            <p>
              As our global network expanded, we established our presence in the <span className="text-[#0E1B2E] font-medium">United Arab Emirates</span>, positioning JS Group at the heart of one of the world's leading aviation hubs. Today, we proudly serve airlines, Flight Training Organisations (FTOs), aircraft owners, operators, and asset management companies worldwide.
            </p>
            <p>
              Our reputation has been built on reliability, professionalism, and delivering results. Whether facilitating complex aircraft transactions or providing strategic aviation solutions, JS Group remains committed to creating long-term partnerships founded on trust and excellence.
            </p>

            <div className="mt-14 grid grid-cols-3 gap-6 pt-10 border-t border-[#0E1B2E]/10">
              {[
                ["Founded", "Hong Kong"],
                ["Regional HQ", "Sharjah, UAE"],
                ["Global Reach", "6 Regions"],
              ].map(([k, v]) => (
                <div key={k}>
                  <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#9AA3AE]">{k}</div>
                  <div className="mt-2 font-serif text-[20px] md:text-[24px] text-[#0E1B2E]">{v}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Directors */}
      <section data-testid="about-directors" className="border-t border-[#0E1B2E]/10 py-20 md:py-32 bg-white">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-14">
            <div>
              <div className="flex items-center gap-3 text-[#A5854C] mb-6">
                <div className="hairline w-12"></div>
                <span className="font-mono text-[11px] uppercase tracking-[0.35em]">Leadership</span>
              </div>
              <motion.h2
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.4 }}
                transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
                className="font-serif font-light tracking-[-0.02em] text-[#0E1B2E] text-[36px] md:text-[56px] leading-[1.02] max-w-3xl"
              >
                The people who <span className="italic text-[#A5854C]">steward the firm.</span>
              </motion.h2>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                name: "Ashish",
                role: "Chief Executive Officer",
                initials: "A",
                label: "Director",
                bio: "Ashish has been associated with the aviation industry since the age of 18, building extensive experience across aviation operations, aircraft acquisitions, aviation brokerage, strategic partnerships, and business development. With a strong understanding of both the operational and commercial aspects of the industry, he founded JS Aviation Group with the vision of creating a trusted global platform for aircraft transactions and aviation advisory services. As Chief Executive Officer, Ashish leads the company's strategic direction, international expansion, and client relationships — delivering tailored solutions to airlines, aircraft owners, FTOs, and asset management companies worldwide.",
              },
              {
                name: "Neil Jacob",
                role: "Chief Operating Officer",
                initials: "NJ",
                label: "Director",
                bio: "Neil Jacob brings over 35 years of leadership and commercial experience across the aviation and telecommunications industries. Throughout his career, he has built a strong reputation for strategic planning, business development, and client relationship management. As Chief Operating Officer of JS Aviation Group, he oversees the company's day-to-day operations — ensuring seamless execution of transactions while driving operational excellence, sustainable growth, and long-term partnerships across global markets.",
              },
              {
                name: "Sam Issac",
                role: "President — Aircraft Acquisition & Sales",
                initials: "SI",
                label: "Leadership",
                bio: "Sam Issac brings over 25 years of experience across the global commercial aviation industry, having held senior financial and strategic leadership positions with major airlines, LCCs, and aviation businesses across multiple markets. His career has been focused largely on airline finance, fleet strategy, aircraft financing, restructuring, and business transformation — giving him a deep understanding of both the commercial and financial aspects of aircraft ownership and operations. He has led multi-billion-dollar fleet financing and ACMI programmes, developed financial and operational models for airline start-ups, strengthened governance and financial controls, and played a key role in airline turnaround and profitability initiatives. With this extensive airline and finance background, Sam now brings his expertise to aircraft acquisition and sales, advising clients on fleet opportunities, aircraft transactions, valuation, financing structures, and strategic acquisition decisions.",
              },
            ].map((d, i) => (
              <motion.article
                key={d.name}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.9, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] }}
                className="border border-[#0E1B2E]/10 bg-white p-8 md:p-10 hover:border-[#C8A97E] transition-colors duration-500"
                data-testid={`director-${d.name.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <div className="flex items-start gap-6">
                  <div className="shrink-0 w-16 h-16 md:w-20 md:h-20 border border-[#C8A97E]/60 flex items-center justify-center bg-[#FAFAF9]">
                    <span className="font-serif text-[24px] md:text-[28px] text-[#A5854C]">{d.initials}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#9AA3AE]">
                      {String(i + 1).padStart(2, "0")} / {d.label || "Director"}
                    </div>
                    <h3 className="mt-3 font-serif text-[28px] md:text-[36px] text-[#0E1B2E] tracking-tight leading-tight">{d.name}</h3>
                    <div className="mt-1 text-[#A5854C] text-[13px] tracking-tight">{d.role}</div>
                  </div>
                </div>
                <p className="mt-8 text-[#445060] text-[14px] md:text-[15px] leading-[1.8]">{d.bio}</p>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      <Marquee />
      <CTA />
    </main>
  );
}
